import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  User,
  FileText,
  Stethoscope,
  CheckCircle,
  XCircle,
  ArrowLeft
} from 'lucide-react';
import api from './api';

const DoctorConsultationForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const patientId = state?.patientId || '';
  const patientName = state?.patientName || '';
  const technicianId = state?.technicianId || '';
  const serviceId = state?.serviceId || '';

  const [patient, setPatient] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    patient_unique_id: patientId || '',
    has_medical_conditions: 'No',
    medical_conditions: '',
    has_medications: 'No',
    medications: '',
    has_allergies: 'No',
    allergies: '',
    chief_complaint: '',
    history: '',
    diagnostic_tests: '',
    advice: '',
    fitness_status: [],
    unfit_reason: ''
  });

  useEffect(() => {
    if (!patientId || !patientName) {
      setError("Missing patient information. Please go back and try again.");
      setInitialLoading(false);
      return;
    }

    const fetchInitialData = async () => {
      try {
        setInitialLoading(true);

        // Fetch patient details using unique_patient_id
        const patientRes = await api.get(`/campmanager/patient/${patientId}/`);
        const data = patientRes.data;
        
        console.log("Fetched patient details:", data);
        
        if (data) {
          setPatient({
            patient_name: data.patient_name || patientName || 'N/A',
            unique_patient_id: data.unique_patient_id || patientId,
            patient_excel_id: data.patient_excel_id || 'N/A',
            age: data.age || 'N/A',
            gender: data.gender || 'N/A',
            phone: data.contact_number || 'N/A'
          });
        } else {
          // Fallback if patient fetch fails
          console.error("No patient data received");
          setPatient({
            patient_name: patientName,
            unique_patient_id: patientId,
            patient_excel_id: 'N/A',
            age: 'N/A',
            gender: 'N/A',
            phone: 'N/A'
          });
        }

        // Fetch doctor information
        const doctorRes = await api.get('/technician/doctors/');
        if (doctorRes.data) {
          const doctors = doctorRes.data;
          const selectedDoctor = Array.isArray(doctors) ? doctors[0] : doctors;
          setDoctor(selectedDoctor);
        }

      } catch (err) {
        console.error('Init fetch error:', err);
        setError('Failed to fetch patient details.');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [patientId, patientName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name === 'fitness_status') {
      setFormData(prev => {
        let newValues = [...prev.fitness_status];
        
        if (checked) {
          newValues.push(value);
        } else {
          newValues = newValues.filter(v => v !== value);
        }
        
        return { ...prev, fitness_status: newValues };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const submitData = { ...formData, technician_id: technicianId };

      // Convert fitness_status array to string
      if (Array.isArray(submitData.fitness_status)) {
        if (submitData.fitness_status.length === 0) {
          submitData.fitness_status = 'FIT'; // Default to FIT if nothing selected
        } else if (submitData.fitness_status.length === 1) {
          submitData.fitness_status = submitData.fitness_status[0]; // Single value: "FIT" or "UNFIT"
        } else {
          // Both selected: join with comma or send first value
          submitData.fitness_status = submitData.fitness_status.join(','); // "FIT,UNFIT"
        }
      }

      // Fields that must stay as-is
      const requiredFields = [
        'patient_unique_id', 
        'has_medical_conditions', 
        'has_medications', 
        'has_allergies', 
        'fitness_status'
      ];

      // Convert null/undefined to empty string for optional fields
      Object.keys(submitData).forEach((key) => {
        if (!requiredFields.includes(key) && (submitData[key] === null || submitData[key] === undefined)) {
          submitData[key] = '';
        }
      });

      // Clear dependent fields based on conditions
      if (submitData.has_medical_conditions === 'No') submitData.medical_conditions = '';
      if (submitData.has_medications === 'No') submitData.medications = '';
      if (submitData.has_allergies === 'No') submitData.allergies = '';
      if (!formData.fitness_status.includes('UNFIT')) submitData.unfit_reason = '';
      
      console.log("Submitting consultation payload:", submitData);

      // Submit consultation
      const response = await api.post('/technician/doctor-consultation/', submitData);
      const result = response.data;
      console.log('✅ Doctor consultation created successfully:', result);

      // Mark service as completed
      if (serviceId && technicianId) {
        const completeResponse = await api.post('/technician/submit/', {
          patient_id: patientId,
          technician_id: technicianId,
          service_id: serviceId,
        });

        console.log("✅ Service marked as completed:", completeResponse.data);
      }

      setSuccess(true);

      // Navigate back after 2 seconds
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (err) {
      console.error('❌ Submission error:', err);
      setError(err.response?.data?.message || err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Error state for missing patient information
  if (!patientId || !patientName) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-300 p-6 rounded mb-6">
            <p className="text-red-800 font-semibold">Error</p>
            <p className="text-red-600">Missing patient information. Please go back and select a patient.</p>
            <button
              onClick={handleBack}
              className="mt-4 bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600 text-center">
          <div className="mb-4">
            <div className="h-10 w-10 mx-auto rounded-full border-b-2 border-blue-600 animate-spin"></div>
          </div>
          <p>Loading doctor consultation form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Doctor Consultation Form
              </h1>
              <p className="text-gray-600">
                Patient: {patient?.patient_name || patientName} | ID: {patientId}
                {technicianId && (
                  <span className="ml-4 text-sm text-gray-500">
                    Technician ID: {technicianId}
                  </span>
                )}
                {serviceId && (
                  <span className="ml-4 text-sm text-gray-500">
                    Service ID: {serviceId}
                  </span>
                )}
              </p>
            </div>
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="text-green-600" size={20} />
            <span className="text-green-800">
              Consultation submitted successfully!
              {serviceId && " Service marked as completed."}
            </span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <XCircle className="text-red-600" size={20} />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        <div className="space-y-6">
          {/* Patient Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="text-blue-600" size={20} />
              <h2 className="text-xl font-semibold text-gray-900">Patient Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded border">{patient?.patient_excel_id || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded border">{patient?.patient_name || patientName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded border">{patient?.age || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded border">{patient?.gender || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded border">{patient?.phone || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Unique Patient ID</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded border">{patient?.unique_patient_id || patientId || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Medical History */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="text-green-600" size={20} />
              <h2 className="text-xl font-semibold text-gray-900">Medical History</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Do you have any existing medical conditions?</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="has_medical_conditions"
                      value="Yes"
                      checked={formData.has_medical_conditions === 'Yes'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="has_medical_conditions"
                      value="No"
                      checked={formData.has_medical_conditions === 'No'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">If yes, please specify:</label>
                <input
                  type="text"
                  name="medical_conditions"
                  value={formData.medical_conditions}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={formData.has_medical_conditions === 'No'}
                  placeholder="Specify medical conditions..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Are you currently taking any medications?</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="has_medications"
                      value="Yes"
                      checked={formData.has_medications === 'Yes'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="has_medications"
                      value="No"
                      checked={formData.has_medications === 'No'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">If yes, please specify:</label>
                <input
                  type="text"
                  name="medications"
                  value={formData.medications}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={formData.has_medications === 'No'}
                  placeholder="Specify current medications..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Do you have any allergies?</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="has_allergies"
                      value="Yes"
                      checked={formData.has_allergies === 'Yes'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="has_allergies"
                      value="No"
                      checked={formData.has_allergies === 'No'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">If yes, please specify:</label>
                <input
                  type="text"
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={formData.has_allergies === 'No'}
                  placeholder="Specify allergies..."
                />
              </div>
            </div>
          </div>

          {/* Consultation Details */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <Stethoscope className="text-purple-600" size={20} />
              <h2 className="text-xl font-semibold text-gray-900">Consultation Details</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Chief Complaint:</label>
                <textarea
                  name="chief_complaint"
                  value={formData.chief_complaint}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the main complaint or symptoms..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">History (Family/Own):</label>
                <textarea
                  name="history"
                  value={formData.history}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Relevant medical history..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Diagnostic Tests Recommended:</label>
                <textarea
                  name="diagnostic_tests"
                  value={formData.diagnostic_tests}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Recommended tests and investigations..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Advice (Tests/Medicine):</label>
                <textarea
                  name="advice"
                  value={formData.advice}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Medical advice, prescriptions, recommendations..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Medical Fitness Status:</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="fitness_status"
                      value="FIT"
                      checked={formData.fitness_status.includes('FIT')}
                      onChange={handleCheckboxChange}
                      className="mr-2"
                    />
                    FIT
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="fitness_status"
                      value="UNFIT"
                      checked={formData.fitness_status.includes('UNFIT')}
                      onChange={handleCheckboxChange}
                      className="mr-2"
                    />
                    UNFIT
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">If UNFIT, please specify reason:</label>
                <textarea
                  name="unfit_reason"
                  value={formData.unfit_reason}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={!formData.fitness_status.includes('UNFIT')}
                  placeholder="Reason for unfitness..."
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-center">
              <button
                onClick={handleSubmit}
                disabled={loading || !formData.patient_unique_id}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                {loading ? 'Submitting...' : 'Submit Consultation'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorConsultationForm;